import { Button } from '@/components/ui/button';
import { Edit, History, RefreshCw, BookOpen, Eye, Trash2, Download } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import EmbedPopover from './EmbedPopover';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';

import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { renderDate } from '../../../../../shared/renderDate';

import { DataTableProps } from '@/app/pages/policy-admin/components/datatable';
import { getAvailableFeatures } from '@/utils/featuresUtil';

const availableFeatures = getAvailableFeatures();
const policyReader = availableFeatures.find(f => f.name === 'Policy Reader');

export const getColumns = (onActions: DataTableProps<any, any>["onActions"], isReadOnly = false, showTypeColumn = false) => [
  {
    header: "Name",
    accessorKey: "policy_name",
  },
  // Type column - shown when viewing both policies and processes
  ...(showTypeColumn ? [{
    id: 'documentCategory',
    header: (row: any) => (
      <div className="text-center w-full">Type</div>
    ),
    accessorKey: 'policy_metadata.documentCategory',
    cell: ({ row, getValue }: any) => {
      const value = getValue() || row.original.policy_metadata?.policyType;
      // Determine display text and variant based on documentCategory or policyType
      let displayText = 'Policy';
      let badgeVariant: 'policy' | 'process' | 'privacy' | 'notice' = 'policy';
      if (value === 'procedure') {
        displayText = 'Process';
        badgeVariant = 'process';
      } else if (value === 'privacy' || row.original.id === 'privacy_policy') {
        displayText = 'Privacy';
        badgeVariant = 'privacy';
      } else if (value === 'collection' || row.original.id === 'collection_notice') {
        displayText = 'Notice';
        badgeVariant = 'notice';
      } else if (value === 'policy' || value === 'document' || value === 'default' || value === 'upload') {
        displayText = 'Policy';
        badgeVariant = 'policy';
      }
      return (
        <div className="text-center w-full">
          <Badge variant={badgeVariant}>{displayText}</Badge>
        </div>
      );
    },
  }] : []),
  // {
  //   header: 'Policy Category',
  //   accessorKey: 'policyCategory',
  // },
  // {
  //   header: 'Date Edited',
  //   accessorKey: 'policy_metadata.lastEdited',
  //   cell: ({ getValue }) => {
  //     const date: Date = getValue(); // already a Date object
  //     return renderDate(date, {
  //       day: 'numeric',
  //       month: 'numeric',
  //       year: 'numeric'
  //     });
  //   }
  // },
  {
    id: 'version',
    header: (row) => (
      <div className="text-center w-full">Version</div>
    ),
    accessorKey: 'policy_version',
    cell: ({ getValue }) => (
      <div className="text-center w-full">{getValue()}</div>
    ),
  },
  {
    id: 'status',
    header: (row) => (
      <div className="text-center w-full">Status</div>
    ),
    accessorKey: 'policy_status',
    cell: ({ row, getValue }) => {
      // DED-1658: Calculate status dynamically based on next review date
      let value = getValue() || row.original?.status || 'Draft';
      
      // If there's a next review date, check if it's overdue
      const nextReviewDate = row.original?.nextReviewDate || row.original?.next_review_date;
      if (nextReviewDate && (value === 'Published' || value === 'Current')) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const reviewDate = new Date(nextReviewDate);
        reviewDate.setHours(0, 0, 0, 0);
        
        // If review date is in the past, mark as Overdue
        if (reviewDate < today) {
          value = 'Overdue';
        }
      }
      
      return (
        <div className="text-center w-full">
          <Badge variant={value?.toString().toLowerCase().replace(' ', '') || 'draft'}>{value}</Badge>
        </div>
      );
    }
  },
  {
    id: 'last_reviewed',
    header: (row) => (
      <div className="text-center w-full">Last Reviewed</div>
    ),
    accessorKey: 'policy_history.last_reviewed',
    cell: ({ getValue }) => {
      const date: Date = getValue(); // already a Date object
      return (
        <div className="text-center w-full">
          {renderDate(date, {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric'
          })}
        </div>
      );
    }
  },
  {
    id: 'policy_owner',
    header: (row) => (
      <div className="text-center w-full">Policy Owner</div>
    ),
    accessorKey: 'policy_history.policy_owner',
    cell: ({ row }) => {
      const userId = row.original?.policy_history?.policy_owner || row.original?.owner || '';
      return <span className="text-center w-full block">{onActions.userIdToName?.[userId] || onActions.userIdToName?.[window.activeTenancy?.tenancyOwner] || userId || 'Unknown'}</span>;
    },
  },
  /* NOTE: Remove as part of DED-111 */
  // {
  //   header: 'Component ID',
  //   accessorKey: 'id',
  // },
  {
    id: 'actions',
    header: (row) => (
      <div className="text-center w-full">Actions</div>
    ),
    cell: ({ row }) => {
      const policyId = row.original.id;
      // Special actions for privacy policy row
      if (policyId === 'privacy_policy') {
        return (
          <div className="flex justify-center gap-1">
            <Button 
              id={TOUR_STEP_IDS.OPEN_POLICY_BUTTON}
              variant="ghost" 
              size="icon" 
              title="Open Privacy Policy" 
              onClick={e => { e.stopPropagation(); onActions.onPrivacyView?.(row.original); }}
            >
              <BookOpen className="h-4 w-4" />
            </Button>
            <Button 
              id={TOUR_STEP_IDS.HISTORY_BUTTON}
              variant="ghost" 
              size="icon" 
              title="Privacy Policy History" 
              onClick={e => { e.stopPropagation(); onActions.onPrivacyHistory?.(row.original); }}
            >
              <History className="h-4 w-4" />
            </Button>
            <Button 
              id={TOUR_STEP_IDS.EDIT_POLICY_BUTTON}
              variant="ghost" 
              size="icon" 
              title="Edit Privacy Policy" 
              onClick={e => { e.stopPropagation(); onActions.onPrivacyEdit?.(row.original); }}
            >
              <Edit className="h-4 w-4" />
            </Button>
            {/* Copy Embed Popover */}
            <EmbedPopover row={row} buttonId={TOUR_STEP_IDS.COPY_EMBED_URL_BUTTON} />
          </div>
        );
      }
      // Special actions for collection notice row
      if (policyId === 'collection_notice') {
        return (
          <div className="flex justify-center gap-1">
            <Button variant="ghost" size="icon" title="Open Collection Notice" onClick={e => { e.stopPropagation(); onActions.onCollectionView?.(row.original); }}>
              <BookOpen className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" title="Collection Notice History" onClick={e => { e.stopPropagation(); onActions.onCollectionHistory?.(row.original); }}>
              <History className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" title="Edit Collection Notice" onClick={e => { e.stopPropagation(); onActions.onCollectionEdit?.(row.original); }}>
              <Edit className="h-4 w-4" />
            </Button>
            {/* Copy Embed Popover */}
            <EmbedPopover row={row} buttonId={TOUR_STEP_IDS.COPY_EMBED_URL_BUTTON} />
          </div>
        );
      }
      // Default actions for all other rows
      // Check if policy has a downloadable file (filePath, fileUploadPath, file_url, or document library policy)
      const isDocumentLibraryPolicy = row.original.policy_metadata?.policyType === 'document';
      const hasDownloadableFile = row.original.filePath || 
        row.original.fileUploadPath || 
        row.original.policy_metadata?.file_url ||
        row.original.file_name ||
        row.original.fileName ||
        isDocumentLibraryPolicy;
      
      return (
        <div className="flex justify-center gap-1">
          {policyReader && !isReadOnly && (
            <Button 
              id={TOUR_STEP_IDS.OPEN_POLICY_BUTTON}
              variant="ghost" 
              size="icon" 
              onClick={() => onActions.onRead(row.original)} 
              title="Read Policy"
            >
              <BookOpen className="h-4 w-4" />
            </Button>
          )}
          {hasDownloadableFile && (
            <Button 
              id={TOUR_STEP_IDS.DOWNLOAD_POLICY_BUTTON}
              variant="ghost" 
              size="icon" 
              onClick={() => onActions.onDownload(row.original)} 
              title="Download Policy"
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
          <Button 
            id={TOUR_STEP_IDS.HISTORY_BUTTON}
            variant="ghost" 
            size="icon" 
            onClick={(e) => { e.stopPropagation(); onActions.onViewHistory(row.original); }} 
            title="View History"
          >
            <History className="h-4 w-4" />
          </Button>
          <Button 
            id={TOUR_STEP_IDS.EDIT_POLICY_BUTTON}
            variant="ghost" 
            size="icon" 
            onClick={() => {
              console.log('Edit', row.original);
              onActions.onEdit(row.original);
            }} 
            title={isReadOnly ? "View" : "Edit"}
          >
            {isReadOnly ? <Eye className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
          </Button>
          {!isReadOnly && onActions.onDelete && (
            <Button 
              id={TOUR_STEP_IDS.DELETE_POLICY_BUTTON}
              variant="ghost" 
              size="icon" 
              onClick={(e) => {
                e.stopPropagation();
                onActions.onDelete(row.original);
              }} 
              title="Delete"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      );
    },
  },
  /* HIDDEN COLUMNS FOR SORTING PURPOSES ONLY */
  {
    id: 'type',
    accessorKey: 'policy_metadata.policyType',
    header: 'Type',
    enableColumnFilter: true,
    meta: {
      hidden: true,
    },
  }
];
